import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import './ProductListItem.css';
import no_image from '../../../../assets/images/banners/coleccion-todos1.jpg';

import { productActive } from '../../../../actions/product';
import {
  getInventoryStatus, getProductStock, getProductVariants,
  getVariantPrice, getVariantStock, shouldShowRefrigeratedMedicineNotice
} from '../../../../helpers/admin/productListHelper';
import { GOOGLE_DRIVE_CDN } from '../../../../constants/constants';

import { Box, Chip, Divider, Grid, IconButton, Stack, Typography } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';

const moneyFormat = new Intl.NumberFormat('es-MX', {
  style: 'currency', currency: 'MXN'
});

const getStockLabel = (stock) => {
  const status = getInventoryStatus(stock);

  if (status === 'out')
    return { label: 'Agotado', color: 'error' };
  if (status === 'low')
    return { label: 'Stock bajo', color: 'warning' };
  return { label: 'Disponible', color: 'success' };
};

const VariantRow = ({ variant }) => {
  const stock = getVariantStock(variant);
  const status = getStockLabel(stock);

  return <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', py: .65 }}>
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap variant="body2">{variant.variant_title || 'Variante sin nombre'}</Typography>
      <Typography color="text.secondary" variant="caption">
        {moneyFormat.format(getVariantPrice(variant))}
      </Typography>
    </Box>
    <Stack alignItems="flex-end" spacing={.25}>
      <Typography variant="body2">{`${stock} u.`}</Typography>
      <Chip color={status.color} label={status.label} size="small" variant="outlined" />
    </Stack>
  </Box>;
};

const mexicoDate = value => value
  ? new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Mexico_City'
  }).format(new Date(value))
  : '';

export default function ProductListItemCard({
  product,
  sectionKey,
  anchorEl,
  setAnchorEl,
  archived = false
}) {
  const dispatch = useDispatch();
  const { active } = useSelector(state => state.product);
  const {
    _id, title, category, prod_category, brand
  } = product;
  const variants = getProductVariants(product);
  const totalStock = getProductStock(product);
  const open = Boolean(anchorEl);
  const ariaControls = `product-list-menu-${_id}`;
  const categories = prod_category?.length > 0
    ? prod_category.map(({ category: name }) => name)
    : category || [];

  const handleSettingsClick = ({ currentTarget }) => {
    if (JSON.stringify(active) !== JSON.stringify(product))
      dispatch(productActive(product));
    setAnchorEl(currentTarget);
  };

  const firstImage = variants[0]?.images?.[0];

  return <Grid item xxl={2.4} xl={3} lg={4} md={6} sm={6} xs={12} sx={{ display: 'flex' }}>
    <Box sx={{
      backgroundColor: archived ? '#fff7f7' : '#fff',
      border: archived ? '1px solid #d97979' : 'none',
      boxShadow: '0 2px 4px 2px rgb(0 0 0 / 20%)',
      borderRadius: '7px',
      width: '100%'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: .5 }}>
        <IconButton aria-label="Configuración del producto"
          aria-controls={open ? ariaControls : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleSettingsClick}>
          <SettingsIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 2, pt: 0 }}>
        <Box sx={{ textAlign: 'center' }}>
          <img className="img_product_list_item"
            src={firstImage ? `${GOOGLE_DRIVE_CDN}${firstImage.id}` : no_image}
            width="250px" height="250px" style={{ objectFit: 'contain' }}
            alt={firstImage?.alt || 'producto-nefropolis'} />
        </Box>
        <Typography variant="body1" textAlign="center" sx={{ mt: 1 }}>
          {title}
        </Typography>
        {shouldShowRefrigeratedMedicineNotice(product, sectionKey) && <Box
          sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}
        >
          <Chip
            color="info"
            label="También requiere refrigeración"
            size="small"
            variant="outlined"
          />
        </Box>}
        {archived && <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Chip color="error" label="Retirado de la tienda" size="small" />
          <Typography color="text.secondary" variant="caption" display="block" sx={{ mt: .5 }}>
            {`${mexicoDate(product.archived_at)}${
              product.archived_by?.name ? ` · ${product.archived_by.name}` : ''
            }`}
          </Typography>
        </Box>}
        <Stack alignItems="center" direction="row" justifyContent="center" spacing={.75} sx={{ my: 1 }}>
          {brand && <Chip label={brand} size="small" color="primary" />}
          <Chip label={`${totalStock} existencias`} size="small" variant="outlined" />
        </Stack>
        {variants.length === 1 ? <VariantRow variant={variants[0]} /> : <>
          <Typography color="text.secondary" variant="caption">
            {`${variants.length} variantes`}
          </Typography>
          <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
            {variants.map((variant, index) => <React.Fragment key={variant._id || index}>
              <VariantRow variant={variant} />
              {index < variants.length - 1 && <Divider />}
            </React.Fragment>)}
          </Box>
        </>}
        <Typography sx={{ fontSize: 14, fontWeight: '600', mt: 2 }} gutterBottom>
          Categorías:
        </Typography>
        <Box display="flex" justifyContent="flex-start" flexWrap="wrap" gap="10px" mb={2}>
          {categories.length > 0 ? categories.map((name, index) => <Chip
            key={`${name}-${index}`}
            label={name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()}
            sx={{ backgroundColor: '#7fb3d5', color: '#fff' }}
            size="small"
          />) : 'N/A'}
        </Box>
      </Box>
    </Box>
  </Grid>;
};
